import * as R from "ramda";
import * as React from "react";
import * as Autosuggest from "react-autosuggest";
import { FaGlobe, FaPlus, FaTrash } from "react-icons/fa";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled from "styled-components";
import { AddButton, ButtonRow, CustomSearchInput, SearchField } from "../../../../App/Common/Components/FormComponents";
import { SectionTitle } from "../../../../App/Common/Styles/containers";
import { IState } from "../../../../App/reducer";
import { IOrganization } from "../../../../reducers/course-author/types";
import { Creators } from "../../../../sagas/course-author/watchers";
import { contains } from "../../../../utils";
import {
  deleteElementPatch,
  insertElementPatch,
  patchPayloadCreator,
} from "../../../course-author/helpers/patches/creators";
import { IFullCourse } from "../../../course-author/types";
import ActionConfirmationButton from "../ActionConfirmation";
import AutoComplete from "../Autocomplete";
import { FormContainer } from "../FormElements";

const OrgRow = styled.div`
  display: flex;
`;

const StyledOrgLink = styled.a`
  align-items: center;
  display: flex;
  flex-direction: row;
  padding: 0.5rem;

  color: inherit;
  text-decoration: inherit;

  width: 85rem;
  width: stretch;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

const SearchFormContainer = styled(FormContainer)`
  margin: 2rem 0 1rem 0;
`;

const ListingContainer = styled.div`
  margin: 3rem 0 0 0;
`;

export const OrgName = styled.div`
  margin: 0 2rem;
`;

export interface IEditCourseListingStateProps {
  organizations: IOrganization[];
}

export interface IEditCourseListingDispatchProps {
  addCourseToOrganization: (id: string) => void;
  removeCourseFromOrganization: (id: string) => () => void;
  resolve: (key: string, data: string[]) => void;
}

export interface IEditCourseListingOwnProps {
  course: IFullCourse;
  courseId: string;
  userId: string;
}

export type IEditCourseListingProps = IEditCourseListingOwnProps &
  IEditCourseListingDispatchProps &
  IEditCourseListingStateProps;

export interface IEditCourseListingState {
  displayForm: boolean;
  selected: string | null;
  addOrganization: string;
}

const chooseOrg = "select-organization";
export class EditCourseListing extends React.Component<IEditCourseListingProps, IEditCourseListingState> {
  constructor(props: IEditCourseListingProps) {
    super(props);
    this.state = {
      addOrganization: "",
      displayForm: false,
      selected: null,
    };
  }

  public toggleForm = () => this.setState({ displayForm: !this.state.displayForm });

  public addNewOrganization = () => {
    if (R.isNil(this.state.selected)) {
      return;
    }

    this.props.addCourseToOrganization(this.state.selected);
    this.toggleForm();
  };

  public onSuggestionsFetchRequested = ({ value }: Autosuggest.SuggestionsFetchRequestedParams) => {
    this.setState({ addOrganization: value });
  };

  public render() {
    const orgs: IOrganization[] = this.props.organizations.filter(
      org => !R.isNil(R.find(id => id === org.organizationId, this.props.course.state.listedIn))
    );
    const suggestOrgs = this.props.organizations.filter(org => contains(this.state.addOrganization, org.displayName));

    return (
      <ListingContainer>
        <SectionTitle>Listed in Organisations</SectionTitle>
        <SearchFormContainer>
          <SearchField legend={"Enter Organisation"}>
            <AutoComplete
              fetchSuggestions={this.onSuggestionsFetchRequested}
              htmlName={chooseOrg}
              items={suggestOrgs}
              itemValue={R.propOr("", "displayName")}
              placeholderText="Type here to look for an Organisation"
              setSelected={this.selectNewOrg}
              renderInputComponent={CustomSearchInput}
            />
          </SearchField>
          <ButtonRow width={15} margin={1}>
            <AddButton onClick={this.addNewOrganization}>
              <FaPlus /> Add
            </AddButton>
          </ButtonRow>
        </SearchFormContainer>
        {orgs.map(this.renderOrgRow)}
      </ListingContainer>
    );
  }

  private renderOrgRow: (org: IOrganization) => JSX.Element = org => (
    <OrgRow key={org.organizationId}>
      <StyledOrgLink>
        <FaGlobe />
        <OrgName>{org.displayName}</OrgName>
      </StyledOrgLink>
      <ActionConfirmationButton
        action="Delete"
        actionFn={this.props.removeCourseFromOrganization(org.organizationId)}
        ActionIcon={FaTrash}
      />
    </OrgRow>
  );

  private selectNewOrg = (suggestion: IOrganization) => this.setState({ selected: suggestion.organizationId });
}

const stripSuffix = R.slice(0, 40);

export default connect<
  IEditCourseListingStateProps,
  IEditCourseListingDispatchProps,
  IEditCourseListingOwnProps,
  IState
>(
  (state: IState, ownProps: IEditCourseListingOwnProps) => ({
    organizations: state.App.CourseAuthor._.organizations || [],
  }),
  (dispatch: Dispatch, ownProps: IEditCourseListingOwnProps) => ({
    addCourseToOrganization: (organizationId: string) =>
      dispatch(
        Creators.patchCourse(
          patchPayloadCreator(
            ownProps.course.versionId,
            insertElementPatch(ownProps.course, ownProps.userId, stripSuffix(organizationId), "listedIn")
          )
        )
      ),
    removeCourseFromOrganization: (organizationId: string) => () =>
      dispatch(
        Creators.patchCourse(
          patchPayloadCreator(
            ownProps.course.versionId,
            deleteElementPatch(ownProps.course, ownProps.userId, stripSuffix(organizationId), "listedIn")
          )
        )
      ),
    resolve: (key: string, requestData: string[]) => dispatch(Creators.resolveAuxiliaryData(key, requestData)),
  })
)(EditCourseListing);
